import { isUndefined } from "lodash";
import styled, { css } from "styled-components/native";

import { Theme } from "./styles";
import { getContrastColor } from "./utils";

export interface Greedy {
  greedy?: boolean;
}

export enum FontWeight {
  Light = 300,
  Normal = 400,
  Medium = 500,
  SemiBold = 600,
  Bold = 700,
}

export enum FontSize {
  Small = Theme.spacing * 1.5,
  Normal = Theme.spacing * 2,
  Medium = Theme.spacing * 2.5,
  Large = Theme.spacing * 3,
}

export enum TextAlign {
  Center = "center",
  Left = "left",
}

export enum JustifyContent {
  Center = "center",
  FlexStart = "flex-start",
  FlexEnd = "flex-end",
  SpaceBetween = "space-between",
}

export enum AlignItems {
  Center = "center",
  FlexStart = "flex-start",
  FlexEnd = "flex-end",
  Stretch = "stretch",
}

export interface Background {
  background?: string;
}

export const background = css<Background>`
  ${({ background }) => {
    if (background) {
      return css`
        background: ${background};
      `;
    }
  }}
`;

export interface Color {
  color?: string;
}

export const color = css<Background | Color>`
  color: ${(props) => {
    if ("color" in props) {
      return props.color;
    }

    if ("background" in props) {
      return props.background && getContrastColor(props.background);
    }

    return Theme.colors.text;
  }};
`;

export interface SpacingProps {
  spacing?: number;
  size?: number;
}

export const spacing = css<SpacingProps>`
  padding: ${({ spacing = Theme.spacing, size = 0 }) => {
    return spacing * size;
  }}px;
`;

export const fontSize = css<{ fontSize?: FontSize }>`
  font-size: ${(props) => props.fontSize}px;
  line-height: 175%;
`;

export const fontWeight = css<{ fontWeight?: FontWeight }>`
  font-weight: ${(props) => props.fontWeight};
`;

export interface TextAlignProps {
  textAlign?: TextAlign;
}

export const textAlign = css<TextAlignProps>`
  text-align: ${(props) => props.textAlign};
`;

export const outlineColor = css<{ outlineColor?: string }>`
  outline-color: ${(props) => props.outlineColor || Theme.colors.black};
`;

export const rounded = css<{ roundness?: number }>`
  border-radius: ${(props) =>
    isUndefined(props.roundness) ? Theme.roundness : props.roundness}px;
  overflow: hidden;
`;

export const greedy = css<Greedy>`
  ${(props) => {
    if (props.greedy) {
      return { flex: 1 };
    }
  }}
`;

export const flex = css<{ flex?: number } & Greedy>`
  ${greedy};

  ${(props) => {
    if (props.flex) {
      return { flex: props.flex };
    }
  }}
`;

export interface Full {
  fullWidth?: boolean;
  fullHeight?: boolean;
}

export const full = css<Full>`
  ${(props) => {
    if (props.fullHeight && props.fullWidth) {
      return css`
        width: 100%;
        height: 100%;
      `;
    }

    if (props.fullWidth) {
      return css`
        width: 100%;
      `;
    }

    if (props.fullHeight) {
      return css`
        height: 100%;
      `;
    }
  }}
`;

export const bordered = css`
  border: ${Theme.borderThickness}px solid ${Theme.colors.backgroundDivider};
`;

export const TextInput = styled.TextInput.attrs({
  background: Theme.colors.backgroundInfo,
  fontSize: FontSize.Normal,
  outlineColor: Theme.colors.primary,
})`
  ${background};
  ${color};
  ${fontSize};
  ${outlineColor};
  ${rounded};
  ${bordered};

  padding: 0 ${Theme.spacing}px;
`;

export const Text = styled.Text.attrs<{
  fontWeight?: FontWeight;
  fontSize?: FontSize;
  textAlign?: TextAlign;
}>((props) => ({
  fontWeight: props.fontWeight || FontWeight.Normal,
  fontSize: props.fontSize || FontSize.Normal,
  textAlign: props.textAlign || TextAlign.Left,
}))`
  ${color};
  ${fontWeight};
  ${fontSize};
  ${textAlign};
`;

export const Paragraph = styled(Text)`
  padding-top: ${Theme.spacing}px;
  padding-bottom: ${Theme.spacing}px;
  line-height: 150%;
`;

export const Title = styled(Text).attrs({
  fontSize: FontSize.Large,
})``;

export const SubHeader = styled(Text).attrs({
  fontSize: FontSize.Medium,
})``;

export const Label = styled(Text).attrs({
  fontWeight: FontWeight.Medium,
  fontSize: FontSize.Small,
})`
  text-transform: uppercase;
`;

export const HelperText = styled(Text)``;

export const Divider = styled.View.attrs(({ background }: Background) => ({
  background: background || Theme.colors.backgroundDivider,
}))<{ vertical?: boolean }>`
  ${background};

  ${(props) => {
    if (props.vertical) {
      return css`
        height: 100%;
        width: ${Theme.borderThickness}px;
      `;
    }

    return css`
      height: ${Theme.borderThickness}px;
    `;
  }};
`;

export const Space = styled.View<SpacingProps>`
  ${(props) => {
    const spacing = props.spacing || Theme.spacing;
    const size = props.size || 1;

    return css`
      height: ${spacing * size}px;
      width: ${spacing * size}px;
    `;
  }};
`;

export interface Flexible {
  flex?: React.ReactText;
  alignItems?: string;
  justifyContent?: string;
}

export const flexible = css<Flexible>`
  ${(props) => {
    if (!isUndefined(props.flex)) {
      return css`
        flex: ${props.flex};
      `;
    }
  }}

  align-items: ${(props) => props.alignItems};
  justify-content: ${(props) => props.justifyContent};
`;

export const LayoutBox = styled.View<Full & Greedy & Flexible & SpacingProps>`
  ${full};
  ${greedy};
  ${flexible};
  ${spacing};
`;

export const LayoutRow = styled(LayoutBox)`
  flex-direction: row;
`;

export const LayoutColumn = styled(LayoutBox)`
  flex-direction: column;
`;

export const LayoutGrid = styled(LayoutRow)`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
`;

export const Surface = styled(LayoutBox).attrs({
  background: Theme.colors.background,
})<Greedy>`
  ${background};
`;

export const Info = styled(LayoutBox).attrs({
  background: Theme.colors.backgroundInfo,
})<Roundness>`
  ${background};
  ${rounded};
`;
